import { Box, Button, Divider, Flex } from '@chakra-ui/react'
import { css } from '@emotion/react'
import {
  faBug,
  faBullhorn,
  faChalkboard,
  faFlagAlt,
  faHome,
  faMessageQuestion,
  faSignOut,
  faUser,
} from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { usePathname } from 'next/navigation'

import chakraTokenToVar from '@/utils/chakraTokenToVar'

import UserQ from '@/queries/UserQ'
import { useQuery } from '@/client/context/query'
import { useUser } from '@/client/context/user'
import { trackEvent } from '@/client/services/analytics'
import * as url from '@/client/services/url'
import { isUserDefined, isUserSuspended } from '@/client/services/user'

import AuthLink from '@/components/Auth/AuthLink'
import LogoutLink from '@/components/LogoutLink'
import ShwLink from '@/components/ShwLink'

import UserAvatar from './UserAvatar'

const userMenuStyles = css`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  .menu-target {
    position: relative;
    display: flex;
    align-items: center;
    padding: 4px 6px;
    border-radius: 100px;
    cursor: pointer;
  }

  .menu-target:focus {
    box-shadow: var(--chakra-shadows-outline);
    outline: none;
  }

  .menu-target:hover,
  .menu-target:focus-within {
    .menu-target {
      border-color: ${chakraTokenToVar('border')};
    }
    .options {
      display: block;
    }
  }
  .options {
    display: none;
    position: absolute;
    top: 100%;
    right: 0px;
    min-width: 200px;
    background: ${chakraTokenToVar('bg')};
    box-shadow: 0 5px 10px rgba(0, 0, 0, 0.13);
    border-radius: 3px;
    overflow: hidden;
    user-select: none;

    border-radius: 8px;
    border: 2px solid ${chakraTokenToVar('border.emphasized')};
    box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.15);

    .option {
      display: block;
      color: ${chakraTokenToVar('fg')};
      padding: 10px 20px;
      width: 100%;
      text-align: left;
      white-space: nowrap;
      &:hover {
        background: ${chakraTokenToVar('bg.muted')};
        color: ${chakraTokenToVar('fg')};
        text-decoration: none;
      }
    }
  }
`

function handleClick() {
  trackEvent({
    category: 'Button Click',
    action: 'Navbar Sign In',
  })
}

export default function UserMenu(): React.ReactElement {
  const user = useUser()
  const query = useQuery()
  const pathname = usePathname()
  const userIsSuspended = isUserSuspended(user)

  const me =
    isUserDefined(user) && query != null
      ? (query.one(
          UserQ.where({
            id: user?.id,
          })
        ) ?? null)
      : null
  // ^ Delicate query. Don't change it. If you do, double check SSR for uncached-query warnings
  const userAvatarUrl = me?.(UserQ.avatar_url)
  const userProgressionLevel = me?.(UserQ.progression_level) ?? null

  return (
    <div css={userMenuStyles}>
      {user != null ? (
        user.isLoggedOut ? (
          <Flex alignItems="center" gap="16px">
            <AuthLink mode="login" returnTo={pathname ?? null}>
              <Button
                variant="ghost"
                color="primary.fg"
                _hover={{ color: 'primary.emphasized' }}
                onClick={handleClick}
                textDecoration="none"
                minWidth="70px"
              >
                Sign In
              </Button>
            </AuthLink>
            <AuthLink mode="signup" returnTo={pathname ?? null}>
              <Button
                ml="8px"
                onClick={handleClick}
                textDecoration="none"
                display={{ base: 'none', md: 'block' }}
                bgColor="primary.solid"
                boxShadow={`inset 0px 1px 0px 0.5px ${chakraTokenToVar('primary.solid')}`}
                _hover={{
                  bgColor: 'primary.emphasized',
                  boxShadow: `inset 0px 1px 0px 0.5px ${chakraTokenToVar('primary.fg')}`,
                }}
                borderRadius="32px"
                minWidth="unset"
                px="16px"
              >
                Sign Up
              </Button>
            </AuthLink>
          </Flex>
        ) : user.emailVerified ? (
          <Box className="menu-target" tabIndex={0}>
            <UserAvatar
              src={`${userAvatarUrl}`}
              name={user.firstName}
              size="sm"
              progressionLevel={userProgressionLevel}
            />
            <Box className="options">
              <ShwLink href={url.getDashboardUrl()} width="100%">
                <Button
                  mt="8px"
                  fontWeight="normal"
                  borderRadius={0}
                  variant="unstyled"
                  className="option"
                  leftIcon={
                    <Box color="fg.subtle" width="24px">
                      <FontAwesomeIcon icon={faHome} />
                    </Box>
                  }
                >
                  Dashboard
                </Button>
              </ShwLink>
              <ShwLink
                href={
                  userIsSuspended
                    ? url.getSettingsUrl()
                    : url.getUserUrl({ userId: user.id, username: user.username })
                }
                width="100%"
              >
                <Button
                  fontWeight="normal"
                  borderRadius={0}
                  variant="unstyled"
                  className="option"
                  leftIcon={
                    <Box color="fg.subtle" width="24px">
                      <FontAwesomeIcon icon={faUser} />
                    </Box>
                  }
                >
                  {userIsSuspended ? 'Edit Profile' : 'My Profile'}
                </Button>
              </ShwLink>
              <ShwLink href={url.getClassroomsUrl()} width="100%">
                <Button
                  fontWeight="normal"
                  borderRadius={0}
                  variant="unstyled"
                  className="option"
                  leftIcon={
                    <Box color="fg.subtle" width="24px">
                      <FontAwesomeIcon icon={faChalkboard} />
                    </Box>
                  }
                >
                  My Classrooms
                </Button>
              </ShwLink>
              <Divider color="border" my="8px" />
              <ShwLink href="/refer" width="100%">
                <Button
                  fontWeight="normal"
                  borderRadius={0}
                  variant="unstyled"
                  className="option"
                  leftIcon={
                    <Box color="fg.subtle" width="24px">
                      <FontAwesomeIcon icon={faBullhorn} />
                    </Box>
                  }
                >
                  Refer a Friend
                </Button>
              </ShwLink>
              <ShwLink href={url.getCannyBugReportUrl()} width="100%">
                <Button
                  fontWeight="normal"
                  borderRadius={0}
                  variant="unstyled"
                  className="option"
                  leftIcon={
                    <Box color="gray400" width="24px">
                      <FontAwesomeIcon icon={faBug} />
                    </Box>
                  }
                >
                  Report a Bug
                </Button>
              </ShwLink>
              <ShwLink href="/report" width="100%">
                <Button
                  fontWeight="normal"
                  borderRadius={0}
                  variant="unstyled"
                  className="option"
                  leftIcon={
                    <Box color="fg.subtle" width="24px">
                      <FontAwesomeIcon icon={faFlagAlt} />
                    </Box>
                  }
                >
                  Share Safety Concern
                </Button>
              </ShwLink>
              <ShwLink href={url.faq.getUrl()} isExternal width="100%">
                <Button
                  fontWeight="normal"
                  borderRadius={0}
                  variant="unstyled"
                  className="option"
                  leftIcon={
                    <Box color="fg.subtle" width="24px">
                      <FontAwesomeIcon icon={faMessageQuestion} />
                    </Box>
                  }
                >
                  Help Desk
                </Button>
              </ShwLink>
              <Divider color="border" />
              <LogoutLink width="100%">
                <Button
                  fontWeight="normal"
                  borderRadius={0}
                  mb="8px"
                  variant="unstyled"
                  className="option"
                  leftIcon={
                    <Box color="fg.subtle" width="24px">
                      <FontAwesomeIcon icon={faSignOut} />
                    </Box>
                  }
                >
                  Log out
                </Button>
              </LogoutLink>
            </Box>
          </Box>
        ) : (
          <LogoutLink variant="unstyled" className="option">
            Log out
          </LogoutLink>
        )
      ) : null}
    </div>
  )
}
